import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_CONFIG } from '../config/api';
import { 
  CpuChipIcon,
  ChatBubbleLeftRightIcon,
  BoltIcon,
  ClockIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

const RecentActivityFeed = ({ limit = 10, onAgentClick, compact = false }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchRecentActivity = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_CONFIG.API_BASE}/recent-activity?limit=${limit}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        setActivities(response.data.activities);
      } else {
        setError('Failed to load recent activities');
      }
    } catch (err) {
      console.error('Error fetching recent activity:', err);
      setError('Unable to load recent activities');
    } finally {
      setLoading(false);
    }
  };

  const handleAgentClick = (agentName) => {
    if (onAgentClick && typeof onAgentClick === 'function') {
      onAgentClick(agentName);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchRecentActivity();
  }, [limit]);

  const getIcon = (iconName) => {
    switch (iconName) {
      case 'CpuChipIcon':
        return CpuChipIcon;
      case 'ChatBubbleLeftRightIcon':
        return ChatBubbleLeftRightIcon;
      case 'BoltIcon':
        return BoltIcon;
      default:
        return ClockIcon;
    }
  };

  const getColorClasses = (color) => {
    switch (color) {
      case 'blue':
        return 'bg-blue-100 text-blue-600';
      case 'green':
        return 'bg-green-100 text-green-600';
      case 'purple':
        return 'bg-purple-100 text-purple-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'Unknown time';
    
    const now = new Date();
    const activityTime = new Date(timestamp);
    const diffMs = now - activityTime;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    if (diffDays < 30) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
    
    return activityTime.toLocaleDateString();
  };

  const getDotColor = (color) => {
    switch (color) {
      case 'blue': return 'bg-blue-500';
      case 'green': return 'bg-green-500';
      case 'purple': return 'bg-purple-500';
      default: return 'bg-gray-400';
    }
  };

  if (loading) {
    return (
      <div className={compact ? '' : 'bg-white p-6 rounded-lg border shadow-sm'}>
        <div className="flex items-center justify-between mb-4">
          {!compact && <h3 className="text-xl font-semibold text-gray-800">Recent Activity Feed</h3>}
          <ArrowPathIcon className="w-5 h-5 text-gray-400 animate-spin ml-auto" />
        </div>
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="flex items-center gap-3 animate-pulse">
              <div className="w-2 h-2 bg-gray-200 rounded-full flex-shrink-0"></div>
              <div className="flex-1 h-3.5 bg-gray-200 rounded w-3/4"></div>
              <div className="h-3 bg-gray-100 rounded w-12 flex-shrink-0"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={compact ? '' : 'bg-white p-6 rounded-lg border shadow-sm'}>
        {!compact && (
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-gray-800">Recent Activity Feed</h3>
            <button onClick={fetchRecentActivity} className="text-blue-600 text-sm font-medium flex items-center gap-1">
              <ArrowPathIcon className="w-4 h-4" /> Retry
            </button>
          </div>
        )}
        <div className="text-center py-6 text-gray-400 text-sm">Unable to load activities</div>
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <div className={compact ? '' : 'bg-white p-6 rounded-lg border shadow-sm'}>
        {!compact && (
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-gray-800">Recent Activity Feed</h3>
          </div>
        )}
        <div className="text-center py-6 text-gray-400 text-sm">No recent activities yet</div>
      </div>
    );
  }

  /* ── Compact mode (for home page sidebar) ─────────────────────── */
  if (compact) {
    return (
      <div className="divide-y divide-gray-50">
        {activities.map((activity, index) => (
          <div
            key={`${activity.type}-${activity.item_id}-${index}`}
            className="flex items-center gap-3 py-2.5 hover:bg-gray-50 -mx-1 px-1 rounded-lg transition-colors cursor-default"
          >
            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${getDotColor(activity.color)}`}></span>
            <span className="flex-1 text-xs text-gray-700 truncate leading-tight">{activity.title}</span>
            <span className="text-xs text-gray-400 flex-shrink-0 whitespace-nowrap">{formatTimeAgo(activity.timestamp)}</span>
          </div>
        ))}
      </div>
    );
  }

  /* ── Full mode ────────────────────────────────────────────────── */
  return (
    <div className="bg-white p-6 rounded-lg border shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-gray-800">Recent Activity Feed</h3>
        <button
          onClick={fetchRecentActivity}
          className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1 transition-colors"
        >
          <ArrowPathIcon className="w-4 h-4" />
          Refresh
        </button>
      </div>

      <div className="space-y-4">
        {activities.map((activity, index) => {
          const IconComponent = getIcon(activity.icon);
          const colorClasses = getColorClasses(activity.color);

          return (
            <div key={`${activity.type}-${activity.item_id}-${index}`} className="flex items-start space-x-4 p-4 hover:bg-gray-50 rounded-lg transition-colors">
              <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${colorClasses}`}>
                <IconComponent className="w-5 h-5" />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-gray-900 truncate">
                    {activity.title}
                  </h4>
                  <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
                    {formatTimeAgo(activity.timestamp)}
                  </span>
                </div>

                <p className="text-sm text-gray-600 mt-1">
                  {activity.description}
                </p>

                {activity.agent_name && (
                  <div className="flex items-center mt-2">
                    <button
                      onClick={() => handleAgentClick(activity.agent_name)}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 hover:text-blue-900 transition-colors cursor-pointer"
                    >
                      <CpuChipIcon className="w-3 h-3 mr-1" />
                      {activity.agent_name}
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {activities.length >= limit && (
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">
            Showing latest {activities.length} activities
          </p>
        </div>
      )}
    </div>
  );
};

export default RecentActivityFeed;



